/**
 * Comprehensive Office Hours Validation Utilities
 *
 * Provides robust validation for office hours and lobby closure hours
 * with detailed error messages and edge case handling.
 */

import { OfficeHoursInput } from '../types/practices/types';
import { isValid24hTime, timeToMinutes } from './timeConversion';

export interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

/**
 * Validates a single time string
 */
export const validateTimeString = (time: string, fieldName: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!time || time.trim() === '') {
    errors.push({
      field: fieldName,
      message: `${fieldName} is required`,
      severity: 'error'
    });
    return errors;
  }

  if (!isValid24hTime(time)) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be in valid 24-hour format (HH:MM)`,
      severity: 'error'
    });
  }

  return errors;
};

/**
 * Validates lobby closure hours configuration
 */
export const validateLunchHours = (lunchHours: {
  start: string;
  end: string;
  startDisplay: string;
  endDisplay: string;
}): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Validate start time
  errors.push(
    ...validateTimeString(
      lunchHours.start,
      'Lobby closure start time'
    )
  );

  // Validate end time
  errors.push(
    ...validateTimeString(lunchHours.end, 'Lobby closure end time')
  );

  // If both times are valid, check logical consistency
  if (errors.length === 0) {
    const startMinutes = timeToMinutes(lunchHours.start);
    const endMinutes = timeToMinutes(lunchHours.end);

    if (startMinutes >= endMinutes) {
      errors.push({
        field: 'lunchHours',
        message:
          'Lobby closure start time must be before lobby closure end time',
        severity: 'error',
      });
    }

    // Check for reasonable lobby closure duration (not too short or too long)
    const duration = endMinutes - startMinutes;
    if (duration < 15) {
      errors.push({
        field: 'lunchHours',
        message:
          'Lobby closure duration should be at least 15 minutes',
        severity: 'warning',
      });
    } else if (duration > 180) {
      errors.push({
        field: 'lunchHours',
        message: 'Lobby closure duration should not exceed 3 hours',
        severity: 'warning',
      });
    }
  }

  return errors;
};

/**
 * Validates a single day's office hours
 */
export const validateDayOfficeHours = (day: OfficeHoursInput): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (day.closed) {
    return errors; // Closed days don't need validation
  }

  // Validate open time
  errors.push(...validateTimeString(day.open, `${day.day} open time`));

  // Validate close time
  errors.push(...validateTimeString(day.close, `${day.day} close time`));

  // If both times are valid, check logical consistency
  if (errors.length === 0) {
    const openMinutes = timeToMinutes(day.open);
    const closeMinutes = timeToMinutes(day.close);

    if (openMinutes >= closeMinutes) {
      errors.push({
        field: `${day.day.toLowerCase()}Hours`,
        message: `${day.day}: Open time must be before close time`,
        severity: 'error'
      });
    }

    // Check for reasonable business hours
    const duration = closeMinutes - openMinutes;
    if (duration < 60) {
      errors.push({
        field: `${day.day.toLowerCase()}Hours`,
        message: `${day.day}: Office hours should be at least 1 hour`,
        severity: 'warning'
      });
    } else if (duration > 1440) {
      errors.push({
        field: `${day.day.toLowerCase()}Hours`,
        message: `${day.day}: Office hours cannot exceed 24 hours`,
        severity: 'error'
      });
    }

    // Check for very early or very late hours
    if (openMinutes < 300) { // Before 5 AM
      errors.push({
        field: `${day.day.toLowerCase()}Hours`,
        message: `${day.day}: Very early opening time (before 5 AM)`,
        severity: 'warning'
      });
    }

    if (closeMinutes > 1320) { // After 10 PM
      errors.push({
        field: `${day.day.toLowerCase()}Hours`,
        message: `${day.day}: Very late closing time (after 10 PM)`,
        severity: 'warning'
      });
    }
  }

  return errors;
};

/**
 * Validates lobby closure hours compatibility with office hours
 */
export const validateLunchCompatibility = (
  officeHoursInput: OfficeHoursInput[],
  lunchHours: { start: string; end: string }
): ValidationError[] => {
  const errors: ValidationError[] = [];

  if (!lunchHours.start || !lunchHours.end) {
    return errors;
  }

  const lunchStartMinutes = timeToMinutes(lunchHours.start);
  const lunchEndMinutes = timeToMinutes(lunchHours.end);

  const compatibleDays: string[] = [];
  const incompatibleDays: string[] = [];

  officeHoursInput.forEach(day => {
    if (day.closed || !day.open || !day.close) {
      return;
    }

    const openMinutes = timeToMinutes(day.open);
    const closeMinutes = timeToMinutes(day.close);

    // Check if lunch hours fit within office hours
    if (openMinutes < lunchStartMinutes && lunchEndMinutes < closeMinutes) {
      compatibleDays.push(day.day);
    } else {
      incompatibleDays.push(day.day);
    }
  });

  if (compatibleDays.length === 0) {
    errors.push({
      field: 'lunchHours',
      message:
        'Lobby closure hours do not fit within any open office hours',
      severity: 'error',
    });
  } else if (incompatibleDays.length > 0) {
    errors.push({
      field: 'lunchHours',
      message: `Lobby closure hours will not be applied to: ${incompatibleDays.join(
        ', '
      )}. These days do not span the full lobby closure period.`,
      severity: 'warning',
    });
  }

  return errors;
};

/**
 * Validates office hours for business logic consistency
 */
export const validateBusinessLogic = (officeHoursInput: OfficeHoursInput[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  const openDays = officeHoursInput.filter(day => !day.closed && day.open && day.close);

  // Check for consecutive closed days (might indicate an error)
  const closedDays = officeHoursInput.filter(day => day.closed);
  if (closedDays.length >= 6) {
    errors.push({
      field: 'officeHours',
      message: 'Most days are closed. Please verify this is correct.',
      severity: 'warning'
    });
  }

  // Check for consistent hours across days
  if (openDays.length > 1) {
    const firstDay = openDays[0];
    const allSameHours = openDays.every(day =>
      day.open === firstDay.open && day.close === firstDay.close
    );

    if (allSameHours) {
      errors.push({
        field: 'officeHours',
        message: 'All open days have identical hours. Consider using a template.',
        severity: 'warning'
      });
    }
  }

  return errors;
};

/**
 * Comprehensive validation of office hours configuration
 */
export const validateOfficeHoursConfiguration = (
  officeHoursInput: OfficeHoursInput[],
  observesLunchHours: boolean,
  lunchHours: { start: string; end: string; startDisplay: string; endDisplay: string }
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Validate each day
  officeHoursInput.forEach(day => {
    const dayErrors = validateDayOfficeHours(day);
    dayErrors.forEach(error => {
      if (error.severity === 'error') {
        errors.push(error);
      } else {
        warnings.push(error);
      }
    });
  });

  // Validate lunch hours if enabled
  if (observesLunchHours) {
    const lunchErrors = validateLunchHours(lunchHours);
    lunchErrors.forEach(error => {
      if (error.severity === 'error') {
        errors.push(error);
      } else {
        warnings.push(error);
      }
    });

    // Validate lunch compatibility
    const compatibilityErrors = validateLunchCompatibility(officeHoursInput, lunchHours);
    compatibilityErrors.forEach(error => {
      if (error.severity === 'error') {
        errors.push(error);
      } else {
        warnings.push(error);
      }
    });
  }

  // Validate business logic
  const businessErrors = validateBusinessLogic(officeHoursInput);
  businessErrors.forEach(error => {
    if (error.severity === 'error') {
      errors.push(error);
    } else {
      warnings.push(error);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Formats validation errors for display
 */
export const formatValidationErrors = (result: ValidationResult): {
  errorMessages: string[];
  warningMessages: string[];
  fieldErrors: Record<string, string[]>;
} => {
  const errorMessages: string[] = [];
  const warningMessages: string[] = [];
  const fieldErrors: Record<string, string[]> = {};

  result.errors.forEach(error => {
    errorMessages.push(error.message);
    if (!fieldErrors[error.field]) {
      fieldErrors[error.field] = [];
    }
    fieldErrors[error.field].push(error.message);
  });

  result.warnings.forEach(warning => {
    warningMessages.push(warning.message);
    if (!fieldErrors[warning.field]) {
      fieldErrors[warning.field] = [];
    }
    fieldErrors[warning.field].push(warning.message);
  });

  return { errorMessages, warningMessages, fieldErrors };
};
